import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Big } from "big.js";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import {
  AwardRow,
  ClaimDecisions,
  DecisionNode,
  isWon,
  leavesOf,
} from "../../shared/judgmentDecisions";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SegmentedControl } from "../general/SegmentedControl";
import { amountOf } from "./CloseStageDialog";

export type Decision = "won" | "lost";

const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;
const button = { textTransform: "none", fontWeight: 600 } as const;
const widths = ["26%", "16%", "24%", "34%"];

/** The choice of a decision: none until the user makes it. */
const DecisionButtons = ({
  title,
  value,
  onChange,
}: {
  title: string;
  value: Decision | undefined;
  onChange: (decision: Decision) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.timeline.judgment.claims",
  });
  return (
    <SegmentedControl<Decision | "">
      ariaLabel={title}
      value={value ?? ""}
      options={[
        { value: "won", label: t("upheld") },
        { value: "lost", label: t("dismissed") },
      ]}
      onChange={(decision) => decision !== "" && onChange(decision)}
    />
  );
};

const Leaves = ({
  node,
  shownOf,
  defendantId,
  decided,
  onDecide,
  depth = 0,
}: {
  node: DecisionNode;
  // who of the defendants shows a decision, as one can concern several
  shownOf: ReadonlyMap<string, string>;
  defendantId: string;
  decided: { [id: string]: Decision };
  onDecide: (id: string, decision: Decision) => void;
  depth?: number;
}) => {
  if (node.children.length > 0)
    return (
      <>
        {node.children.map((child) => (
          <Box key={child.id}>
            {child.children.length > 0 && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: "text.secondary",
                  px: 1.75,
                  pt: 1,
                  pl: 1.75 + depth * 2,
                }}
              >
                {child.title}
              </Typography>
            )}
            <Leaves
              node={child}
              shownOf={shownOf}
              defendantId={defendantId}
              decided={decided}
              onDecide={onDecide}
              depth={depth + 1}
            />
          </Box>
        ))}
      </>
    );
  // a generic issue is one decision, for all defendants
  if (shownOf.get(node.id) !== defendantId) return null;
  return (
    <Stack
      direction="row"
      useFlexGap
      sx={{
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
        justifyContent: "space-between",
        px: 1.75,
        py: 0.75,
        pl: 1.75 + depth * 2,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Typography sx={{ fontSize: 14 }}>{node.title}</Typography>
      <DecisionButtons
        title={node.title}
        value={decided[node.id]}
        onChange={(decision) => onDecide(node.id, decision)}
      />
    </Stack>
  );
};

/**
 * A claim of the judgment: its decisions, per defendant, down to its lowest
 * issues, and what the court awarded where it is upheld.
 */
export const JudgmentClaim = ({
  decisions: { claim, perDefendant },
  decided,
  onDecide,
  rows,
  matched,
  awardOf,
  setAward,
  interest,
  setInterest,
}: {
  decisions: ClaimDecisions;
  decided: { [id: string]: Decision };
  onDecide: (id: string, decision: Decision) => void;
  rows: AwardRow[];
  matched: boolean;
  awardOf: (row: AwardRow) => string;
  setAward: (row: AwardRow, value: string) => void;
  interest: string | undefined;
  setInterest: (value: string) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.timeline.judgment.claims",
  });
  const { currency } = useCurrency();
  const money = (value: Big | number) =>
    currency.format(typeof value === "number" ? value : value.toNumber());
  const won = new Set(
    Object.entries(decided).flatMap(([id, decision]) =>
      decision === "won" ? [id] : [],
    ),
  );
  const shownOf = new Map<string, string>();
  for (const { defendant, root } of perDefendant)
    for (const { id } of leavesOf(root))
      if (!shownOf.has(id)) shownOf.set(id, defendant.id);
  const several = perDefendant.length > 1;
  const isOneDecision = (root: DecisionNode) => root.children.length === 0;
  const statusOf = (root: DecisionNode) => {
    const leaves = leavesOf(root);
    if (leaves.every(({ id }) => decided[id])) {
      return t(isWon(root, won) ? "upheld" : "dismissed");
    }
    return t("undecided");
  };
  const onlyOne = !several && isOneDecision(perDefendant[0].root);
  const defendants = perDefendant.map(({ defendant }) => defendant.name);
  return (
    <Box
      sx={{
        mt: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        useFlexGap
        sx={{
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.75,
          py: 1.25,
          bgcolor: "action.hover",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {claim.title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {claim.claimant.name} v {defendants.join(", ")}
          </Typography>
        </Box>
        {onlyOne ? (
          <DecisionButtons
            title={claim.title}
            value={decided[perDefendant[0].root.id]}
            onChange={(decision) => onDecide(perDefendant[0].root.id, decision)}
          />
        ) : (
          !several && (
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {statusOf(perDefendant[0].root)}
            </Typography>
          )
        )}
      </Stack>
      {!onlyOne &&
        perDefendant.map(({ defendant, root }) => (
          <Box key={defendant.id}>
            {several && (
              <Stack
                direction="row"
                useFlexGap
                sx={{
                  flexWrap: "wrap",
                  gap: 1.5,
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.75,
                  py: 0.75,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {t("against", { name: defendant.name })}
                </Typography>
                {isOneDecision(root) ? (
                  <DecisionButtons
                    title={t("against", { name: defendant.name })}
                    value={decided[root.id]}
                    onChange={(decision) => onDecide(root.id, decision)}
                  />
                ) : (
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                    {statusOf(root)}
                  </Typography>
                )}
              </Stack>
            )}
            <Leaves
              node={root}
              shownOf={shownOf}
              defendantId={defendant.id}
              decided={decided}
              onDecide={onDecide}
            />
          </Box>
        ))}
      {rows.length > 0 && (
        <Box sx={{ overflowX: "auto", borderTop: 1, borderColor: "divider" }}>
          <Table size="small" sx={{ tableLayout: "fixed", minWidth: 640 }}>
            <TableHead>
              <TableRow>
                {(["amount", "claimed", "awarded", "then"] as const).map(
                  (column, index) => (
                    <TableCell
                      key={column}
                      align={column === "claimed" ? "right" : "left"}
                      sx={{ width: widths[index] }}
                    >
                      {t(`column.${column}`)}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const text = awardOf(row);
                const value = amountOf(text);
                const diff = matched ? value?.minus(row.expected) : undefined;
                return (
                  <TableRow key={`${row.issueId}:${row.damageId}`}>
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell align="right" sx={amount}>
                      {money(row.claimed)}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={text}
                        error={text !== "" && value === undefined}
                        onChange={(event) => setAward(row, event.target.value)}
                        slotProps={{
                          htmlInput: {
                            inputMode: "decimal",
                            "aria-label": t("awardedFor", { name: row.name }),
                            style: { textAlign: "right" },
                          },
                        }}
                        sx={{ width: 140 }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 13,
                        whiteSpace: "nowrap",
                        color: diff?.gt(0) ? "warning.dark" : "text.secondary",
                        fontWeight: diff?.gt(0) ? 600 : undefined,
                      }}
                    >
                      {diff === undefined
                        ? t("toEnter")
                        : diff.eq(0)
                          ? t("asOutcome")
                          : diff.lt(0)
                            ? t("lessThanOutcome", {
                                amount: money(diff.abs()),
                              })
                            : t("moreThanOutcome", { amount: money(diff) })}
                    </TableCell>
                  </TableRow>
                );
              })}
              {interest !== undefined && (
                <TableRow>
                  <TableCell component="th" scope="row">
                    {t("interest")}
                  </TableCell>
                  <TableCell />
                  <TableCell>
                    <TextField
                      size="small"
                      value={interest}
                      error={
                        interest !== "" && amountOf(interest) === undefined
                      }
                      onChange={(event) => setInterest(event.target.value)}
                      slotProps={{
                        htmlInput: {
                          inputMode: "decimal",
                          "aria-label": t("interest"),
                          style: { textAlign: "right" },
                        },
                      }}
                      sx={{ width: 140 }}
                    />
                  </TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {interest === undefined && (
            <Button
              size="small"
              sx={{ ...button, m: 0.75 }}
              onClick={() => setInterest("")}
            >
              + {t("addInterest")}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
